// Utilities
import { consoleWarn, createRange, isNullOrUndefined, isString } from "../../../util/index.js"; // Types
export function weekInfo(locale) {
  // https://simplelocalize.io/data/locales/
  // then `new Intl.Locale(...).getWeekInfo()`
  const code = locale.slice(-2).toUpperCase();
  switch (true) {
    case locale === 'GB-alt-variant':
      {
        return {
          firstDay: 0,
          firstWeekSize: 4
        };
      }
    case locale === '001':
      {
        return {
          firstDay: 1,
          firstWeekSize: 1
        };
      }
    case `AG AS BD BR BS BT BW BZ CA CO DM DO ET GT GU HK HN ID IL IN JM JP KE
    KH KR LA MH MM MO MT MX MZ NI NP PA PE PH PK PR PY SA SG SV TH TT TW UM US
    VE VI WS YE ZA ZW`.includes(code):
      {
        return {
          firstDay: 0,
          firstWeekSize: 1
        };
      }
    case `AI AL AM AR AU AZ BA BM BN BY CL CM CN CR CY EC GE HR KG KZ LB LK LV
    MD ME MK MN MY NZ RO RS SI TJ TM TR UA UY UZ VN XK`.includes(code):
      {
        return {
          firstDay: 1,
          firstWeekSize: 1
        };
      }
    case `AD AN AT AX BE BG CH CZ DE DK EE ES FI FJ FO FR GB GF GP GR HU IE IS
    IT LI LT LU MC MQ NL NO PL RE RU SE SK SM VA`.includes(code):
      {
        return {
          firstDay: 1,
          firstWeekSize: 4
        };
      }
    case `AE AF BH DJ DZ EG IQ IR JO KW LY OM QA SD SY`.includes(code):
      {
        return {
          firstDay: 6,
          firstWeekSize: 1
        };
      }
    case code === 'MV':
      {
        return {
          firstDay: 5,
          firstWeekSize: 1
        };
      }
    case code === 'PT':
      {
        return {
          firstDay: 0,
          firstWeekSize: 4
        };
      }
    default:
      return null;
  }
}
export function withWeekFallback(adapter, locales) {
  // TODO: replace with toPlainDate when adopting Temporal
  function toUTCDate(date) {
    return new Date(Date.UTC(adapter.getYear(date), adapter.getMonth(date), adapter.getDate(date)));
  }
  function getWeekday(date) {
    return toUTCDate(date).getUTCDay();
  }
  function getLocale() {
    const code = isString(adapter.locale) ? adapter.locale : adapter.locale?.code;
    try {
      return code && (locales[code] ?? new Intl.Locale(code).maximize().toString());
    } catch {
      consoleWarn(`Invalid locale "${code}", expected BCP 47 tag`);
      return undefined;
    }
  }
  const ownFirstDay = getWeekday(adapter.startOfWeek(adapter.date()));
  const probe = (ownFirstDay + 1) % 7; // 2nd day of the week
  if (getWeekday(adapter.startOfWeek(adapter.date(), probe)) === probe) {
    return adapter; // adapter is fine, bail
  }
  function startOfWeek(date, firstDayOfWeek) {
    if (isNullOrUndefined(firstDayOfWeek)) return adapter.startOfWeek(date);
    let first = Number(firstDayOfWeek) % 7;
    if (![0, 1, 2, 3, 4, 5, 6].includes(first)) {
      consoleWarn('Invalid firstDayOfWeek, expected discrete number in range [0-6]');
      first = 0;
    }
    const offset = (getWeekday(date) - first + 7) % 7;
    return adapter.addDays(adapter.startOfDay(date), -offset);
  }
  function getWeekArray(date, firstDayOfWeek) {
    if (isNullOrUndefined(firstDayOfWeek)) return adapter.getWeekArray(date);
    const end = adapter.endOfMonth(date);
    const weeks = [];
    let start = startOfWeek(adapter.startOfMonth(date), firstDayOfWeek);
    while (adapter.isBefore(start, end)) {
      const weekStart = start;
      weeks.push(createRange(7).map(day => adapter.addDays(weekStart, day)));
      start = adapter.addDays(start, 7);
    }
    return weeks;
  }
  function getWeekdays(firstDayOfWeek, weekdayFormat) {
    const start = startOfWeek(adapter.date(), firstDayOfWeek);
    const days = createRange(7).map(day => adapter.addDays(start, day));
    switch (weekdayFormat) {
      case 'long':
        return days.map(day => adapter.format(day, 'weekday'));
      case 'short':
        return days.map(day => adapter.format(day, 'weekdayShort'));
      case 'narrow':
        {
          // date-io has no unified name for narrow format, so we need a fallback to Intl
          const formatter = new Intl.DateTimeFormat(getLocale(), {
            weekday: 'narrow',
            timeZone: 'UTC'
          });
          return days.map(day => formatter.format(toUTCDate(day)));
        }
    }
    const names = adapter.getWeekdays();
    const shift = (getWeekday(start) - getWeekday(adapter.startOfWeek(adapter.date())) + 7) % 7;
    return [...names.slice(shift), ...names.slice(0, shift)];
  }
  function getWeek(date, firstDayOfWeek, firstDayOfYear) {
    if (isNullOrUndefined(firstDayOfWeek) && isNullOrUndefined(firstDayOfYear)) return adapter.getWeek(date);
    const start = startOfWeek(date, firstDayOfWeek);
    const locale = getLocale();
    const minWeekSize = isNullOrUndefined(firstDayOfYear) ? locale && weekInfo(locale)?.firstWeekSize || 1 : 7 - (Number(firstDayOfYear) - getWeekday(start) + 7) % 7;
    const anchor = toUTCDate(adapter.addDays(start, 7 - minWeekSize));
    const year = anchor.getUTCFullYear();
    const dayOfYear = (anchor.getTime() - Date.UTC(year, 0, 1)) / 864e5;
    return Math.floor(dayOfYear / 7) + 1;
  }
  const overrides = new Map([['startOfWeek', startOfWeek], ['getWeekArray', getWeekArray], ['getWeekdays', getWeekdays], ['getWeek', getWeek]]);
  return new Proxy(adapter, {
    get: (target, key, receiver) => overrides.get(key) ?? Reflect.get(target, key, receiver)
  });
}
//# sourceMappingURL=week.js.map